import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Flex,
  Card,
  Button,
  ButtonGroup,
  Heading,
  Text,
  COLORS,
} from '@podiumhq/design-system';

const leftSideWidth = 50;
const rightSideWidth = 100 - leftSideWidth;

const Layout = ({
  children,
  primaryCta,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  secondaryCta,
  title,
  staticContent,
  subTitle,
  progress,
}) => {
  if (!children) return null;

  const renderChildren = () => {
    const logoBoxProps = {
      primaryCta,
      onPrimaryCtaClick,
      onSecondaryCtaClick,
      secondaryCta,
      title,
      staticContent,
      subTitle,
      progress,
    };

    if (children.length === 2) {
      return (
        <Flex height="100%" width="100%">
          <LogoBox width={`${leftSideWidth}%`} {...logoBoxProps}>
            {children[0]}
          </LogoBox>

          <Box
            height="100%"
            width={`${rightSideWidth}%`}
            overflow="hidden"
            background="#EEECEA"
          >
            {children[1]}
          </Box>
        </Flex>
      );
    }
    return <LogoBox {...logoBoxProps}>{children}</LogoBox>;
  };
  return (
    <Flex alignItems="flex-end" justifyContent="center">
      <Card
        borderBottomLeftRadius="0"
        borderBottomRightRadius="0"
        zIndex="2"
        position="fixed"
        width="93%"
        height="93%"
        bottom="0px"
      >
        {renderChildren()}
      </Card>
    </Flex>
  );
};

const CtaButton = ({ cta, onClick, variant }) => {
  if (!cta) return null;
  return (
    <Button variant={variant} onClick={onClick}>
      {cta}
    </Button>
  );
};

const LogoBox = ({
  children,
  secondaryCta,
  onSecondaryCtaClick,
  width = '100%',
  primaryCta,
  onPrimaryCtaClick,
  title,
  staticContent,
  subTitle,
  progress,
}) => {
  return (
    <Flex
      p="24px"
      boxSizing="border-box"
      height="100%"
      width={width}
      maxHeight="100%"
      background="white"
      flexDirection="column"
      flexWrap="nowrap"
    >
      <Box flexShrink="0">
        <svg
          width="23"
          height="28"
          viewBox="0 0 23 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0.46582V16.968L7.84824 28.4658H16.9394L12.1782 16.968H22.908V0.46582H0Z"
            fill="#12151A"
          />
        </svg>
        {title && (
          <Heading marginBottom="16px" marginTop="32px">
            {title}
          </Heading>
        )}
        {subTitle && (
          <Text color={COLORS.black.lighter} marginBottom="16px">
            {subTitle}
          </Text>
        )}
        {staticContent && staticContent}
      </Box>
      <Box overflow="auto" flexGrow="1">
        {children}
      </Box>
      <Flex
        paddingTop="24px"
        flexShrink="0"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <ProgressBar maxWidth={'147px'} value={progress} />
        <ButtonGroup spacing={4}>
          <CtaButton onClick={onSecondaryCtaClick} cta={secondaryCta} />
          <CtaButton
            variant="primary"
            cta={primaryCta}
            onClick={onPrimaryCtaClick}
          />
        </ButtonGroup>
      </Flex>
    </Flex>
  );
};

const ProgressBar = ({ value, maxWidth }) => {
  return (
    <Box
      background="silver.base"
      borderRadius="8px"
      height="10px"
      width="100%"
      maxWidth={maxWidth ? maxWidth : ''}
    >
      <Box
        position="relative"
        width={`${value}%`}
        background="blue.base"
        height="10px"
        borderRadius="8px"
      />
    </Box>
  );
};

const { func, node, string, number } = PropTypes;

Layout.propTypes = {
  children: node.isRequired,
  title: string,
  subTitle: string,
  primaryCta: string,
  onPrimaryCtaClick: func,
  secondaryCta: string,
  onSecondaryCtaClick: func,
  staticContent: node,
  progress: number,
};

Layout.defaultProps = {
  title: '',
  subTitle: '',
  primaryCta: '',
  onPrimaryCtaClick: () => {},
  secondaryCta: '',
  onSecondaryCtaClick: () => {},
  staticContent: null,
  progress: 0,
};

export default Layout;
